import { InvoiceStatus } from '../invoicing/invoicing.enum';
import { PurchaseOrderStatus } from '../purchase/purchase.order';
import { QuoteSimpleStatus } from '../quotes/quote.pro';
import { SaleOrderStatus } from '../sale.order/sale.order';

export type FinancialDocumentStateTone = 'neutral' | 'warning' | 'brand' | 'success' | 'error';
export type FinancialDocumentEditability = 'editable' | 'conditional' | 'readonly';
export type FinancialDocumentTransitionKind = 'state' | 'system' | 'revision';

export interface FinancialDocumentStateDefinition<S extends string> {
  readonly state: S;
  readonly tone: FinancialDocumentStateTone;
  readonly editability: FinancialDocumentEditability;
  readonly terminal?: boolean;
}

export interface FinancialDocumentTransitionDefinition<S extends string, C extends string> {
  readonly command: C;
  readonly label: string;
  readonly from: readonly S[];
  readonly to: S;
  readonly alternateTo?: readonly S[];
  readonly kind?: FinancialDocumentTransitionKind;
  readonly confirmation?: boolean;
  readonly effect?: string;
}

export interface FinancialDocumentLifecycle<S extends string, C extends string> {
  readonly initial: S;
  readonly states: readonly FinancialDocumentStateDefinition<S>[];
  readonly transitions: readonly FinancialDocumentTransitionDefinition<S, C>[];
}

export function availableFinancialDocumentTransitions<S extends string, C extends string>(lifecycle: FinancialDocumentLifecycle<S, C>, state: S): readonly FinancialDocumentTransitionDefinition<S, C>[] {
  return lifecycle.transitions.filter(transition => transition.from.includes(state));
}

export function resolveFinancialDocumentTransition<S extends string, C extends string>(lifecycle: FinancialDocumentLifecycle<S, C>, state: S, command: C): FinancialDocumentTransitionDefinition<S, C> | undefined {
  return lifecycle.transitions.find(transition => transition.command === command && transition.from.includes(state));
}

export enum QuoteLifecycleCommand {
  SubmitForApproval = 'submitForApproval',
  RequestChanges = 'requestChanges',
  Approve = 'approve',
  MarkSent = 'markSent',
  Accept = 'accept',
  CompletePrepayment = 'completePrepayment',
  Decline = 'decline',
  Cancel = 'cancel',
  Revise = 'revise',
}

export const quoteLifecycle: FinancialDocumentLifecycle<QuoteSimpleStatus, QuoteLifecycleCommand> = {
  initial: QuoteSimpleStatus.Draft,
  states: [
    { state: QuoteSimpleStatus.Draft, tone: 'neutral', editability: 'editable' },
    { state: QuoteSimpleStatus.PendingApproval, tone: 'warning', editability: 'conditional' },
    { state: QuoteSimpleStatus.ChangesRequired, tone: 'warning', editability: 'editable' },
    { state: QuoteSimpleStatus.Approved, tone: 'brand', editability: 'readonly' },
    { state: QuoteSimpleStatus.AwaitingAcceptance, tone: 'brand', editability: 'readonly' },
    { state: QuoteSimpleStatus.AwaitingPayment, tone: 'warning', editability: 'readonly' },
    { state: QuoteSimpleStatus.Accepted, tone: 'success', editability: 'readonly', terminal: true },
    { state: QuoteSimpleStatus.Declined, tone: 'error', editability: 'readonly', terminal: true },
    { state: QuoteSimpleStatus.Cancelled, tone: 'error', editability: 'readonly', terminal: true },
  ],
  transitions: [
    { command: QuoteLifecycleCommand.SubmitForApproval, label: 'Submit for approval', from: [QuoteSimpleStatus.Draft, QuoteSimpleStatus.ChangesRequired], to: QuoteSimpleStatus.PendingApproval },
    { command: QuoteLifecycleCommand.RequestChanges, label: 'Request changes', from: [QuoteSimpleStatus.PendingApproval], to: QuoteSimpleStatus.ChangesRequired, effect: 'Require a changes note.' },
    { command: QuoteLifecycleCommand.Approve, label: 'Approve', from: [QuoteSimpleStatus.Draft, QuoteSimpleStatus.PendingApproval, QuoteSimpleStatus.ChangesRequired], to: QuoteSimpleStatus.Approved },
    { command: QuoteLifecycleCommand.MarkSent, label: 'Mark sent', from: [QuoteSimpleStatus.Approved], to: QuoteSimpleStatus.AwaitingAcceptance, effect: 'Record the first sent date and start acceptance reminders. Invoked directly or by the first successful Email.' },
    { command: QuoteLifecycleCommand.Accept, label: 'Accept', from: [QuoteSimpleStatus.Approved, QuoteSimpleStatus.AwaitingAcceptance], to: QuoteSimpleStatus.Accepted, alternateTo: [QuoteSimpleStatus.AwaitingPayment], effect: 'When policy requires a positive prepayment, atomically create/link its Invoice and use Awaiting Payment instead of Accepted.' },
    { command: QuoteLifecycleCommand.CompletePrepayment, label: 'Complete prepayment', from: [QuoteSimpleStatus.AwaitingPayment], to: QuoteSimpleStatus.Accepted, kind: 'system', effect: 'Require the linked prepayment invoice to be fully paid.' },
    { command: QuoteLifecycleCommand.Decline, label: 'Decline', from: [QuoteSimpleStatus.Approved, QuoteSimpleStatus.AwaitingAcceptance], to: QuoteSimpleStatus.Declined, confirmation: true },
    { command: QuoteLifecycleCommand.Cancel, label: 'Cancel', from: [QuoteSimpleStatus.PendingApproval, QuoteSimpleStatus.ChangesRequired, QuoteSimpleStatus.Approved, QuoteSimpleStatus.AwaitingAcceptance, QuoteSimpleStatus.Accepted], to: QuoteSimpleStatus.Cancelled, confirmation: true },
    { command: QuoteLifecycleCommand.Revise, label: 'Revise', from: [QuoteSimpleStatus.Approved, QuoteSimpleStatus.AwaitingAcceptance, QuoteSimpleStatus.AwaitingPayment, QuoteSimpleStatus.Accepted, QuoteSimpleStatus.Declined, QuoteSimpleStatus.Cancelled], to: QuoteSimpleStatus.Draft, kind: 'revision', effect: 'Cancel the original except when already Declined, then create a new Draft version. Keep any prepayment Invoice linked to the original and omit that link from the revision.' },
  ],
};

export enum PurchaseOrderLifecycleCommand {
  SubmitForApproval = 'submitForApproval',
  RequestChanges = 'requestChanges',
  Approve = 'approve',
  MarkSent = 'markSent',
  Send = 'send',
  GenerateBill = 'generateBill',
  Cancel = 'cancel',
}

export const purchaseOrderLifecycle: FinancialDocumentLifecycle<PurchaseOrderStatus, PurchaseOrderLifecycleCommand> = {
  initial: PurchaseOrderStatus.Draft,
  states: [
    { state: PurchaseOrderStatus.Draft, tone: 'neutral', editability: 'editable' },
    { state: PurchaseOrderStatus.PendingApproval, tone: 'warning', editability: 'conditional' },
    { state: PurchaseOrderStatus.ChangesRequired, tone: 'warning', editability: 'editable' },
    { state: PurchaseOrderStatus.Approved, tone: 'brand', editability: 'readonly' },
    { state: PurchaseOrderStatus.AwaitingDelivery, tone: 'brand', editability: 'readonly' },
    { state: PurchaseOrderStatus.Billed, tone: 'success', editability: 'readonly', terminal: true },
    { state: PurchaseOrderStatus.Cancelled, tone: 'error', editability: 'readonly', terminal: true },
  ],
  transitions: [
    { command: PurchaseOrderLifecycleCommand.SubmitForApproval, label: 'Submit for approval', from: [PurchaseOrderStatus.Draft, PurchaseOrderStatus.ChangesRequired], to: PurchaseOrderStatus.PendingApproval },
    { command: PurchaseOrderLifecycleCommand.RequestChanges, label: 'Request changes', from: [PurchaseOrderStatus.PendingApproval], to: PurchaseOrderStatus.ChangesRequired, effect: 'Require a changes note.' },
    { command: PurchaseOrderLifecycleCommand.Approve, label: 'Approve', from: [PurchaseOrderStatus.Draft, PurchaseOrderStatus.PendingApproval, PurchaseOrderStatus.ChangesRequired], to: PurchaseOrderStatus.Approved },
    { command: PurchaseOrderLifecycleCommand.MarkSent, label: 'Mark as sent', from: [PurchaseOrderStatus.Approved], to: PurchaseOrderStatus.AwaitingDelivery },
    { command: PurchaseOrderLifecycleCommand.Send, label: 'Send purchase order', from: [PurchaseOrderStatus.Approved, PurchaseOrderStatus.AwaitingDelivery], to: PurchaseOrderStatus.AwaitingDelivery, effect: 'Generate and send the supplier document.' },
    { command: PurchaseOrderLifecycleCommand.GenerateBill, label: 'Generate bill', from: [PurchaseOrderStatus.Approved, PurchaseOrderStatus.AwaitingDelivery], to: PurchaseOrderStatus.Billed, effect: 'Create and link the Bill atomically.' },
    { command: PurchaseOrderLifecycleCommand.Cancel, label: 'Cancel', from: [PurchaseOrderStatus.PendingApproval, PurchaseOrderStatus.Approved, PurchaseOrderStatus.AwaitingDelivery], to: PurchaseOrderStatus.Cancelled, confirmation: true },
  ],
};

export enum InvoicingLifecycleCommand {
  Approve = 'approve',
  Retry = 'retry',
  Void = 'void',
  Reissue = 'reissue',
  Sync = 'sync',
}

function createInvoicingLifecycle(): FinancialDocumentLifecycle<InvoiceStatus, InvoicingLifecycleCommand> {
  return {
    initial: InvoiceStatus.Draft,
    states: [
      { state: InvoiceStatus.Draft, tone: 'neutral', editability: 'editable' },
      { state: InvoiceStatus.Approved, tone: 'brand', editability: 'conditional' },
      { state: InvoiceStatus.Error, tone: 'error', editability: 'readonly' },
      { state: InvoiceStatus.Void, tone: 'error', editability: 'readonly', terminal: true },
    ],
    transitions: [
      { command: InvoicingLifecycleCommand.Approve, label: 'Approve', from: [InvoiceStatus.Draft], to: InvoiceStatus.Approved },
      { command: InvoicingLifecycleCommand.Retry, label: 'Retry', from: [InvoiceStatus.Error], to: InvoiceStatus.Approved },
      { command: InvoicingLifecycleCommand.Void, label: 'Void', from: [InvoiceStatus.Draft, InvoiceStatus.Approved, InvoiceStatus.Error], to: InvoiceStatus.Void, confirmation: true },
      { command: InvoicingLifecycleCommand.Reissue, label: 'Reissue', from: [InvoiceStatus.Approved], to: InvoiceStatus.Approved, effect: 'Increment the document version without reintegration.' },
      { command: InvoicingLifecycleCommand.Sync, label: 'Sync payments', from: [InvoiceStatus.Approved], to: InvoiceStatus.Approved },
    ],
  };
}

export const invoiceLifecycle = createInvoicingLifecycle();
export const billLifecycle = createInvoicingLifecycle();

export enum SaleOrderLifecycleCommand {
  ConvertToInvoice = 'convertToInvoice',
  Cancel = 'cancel',
  InvoiceVoided = 'invoiceVoided',
}

export const saleOrderLifecycle: FinancialDocumentLifecycle<SaleOrderStatus, SaleOrderLifecycleCommand> = {
  initial: SaleOrderStatus.New,
  states: [
    { state: SaleOrderStatus.New, tone: 'brand', editability: 'editable' },
    { state: SaleOrderStatus.Invoiced, tone: 'success', editability: 'readonly' },
    { state: SaleOrderStatus.Cancelled, tone: 'error', editability: 'readonly', terminal: true },
  ],
  transitions: [
    { command: SaleOrderLifecycleCommand.ConvertToInvoice, label: 'Convert to invoice', from: [SaleOrderStatus.New], to: SaleOrderStatus.Invoiced, effect: 'Create one Invoice from every section and append its durable link atomically.' },
    { command: SaleOrderLifecycleCommand.Cancel, label: 'Cancel', from: [SaleOrderStatus.New], to: SaleOrderStatus.Cancelled, confirmation: true },
    { command: SaleOrderLifecycleCommand.InvoiceVoided, label: 'Linked invoice voided', from: [SaleOrderStatus.Invoiced], to: SaleOrderStatus.New, kind: 'system', effect: 'Remove only the matching Invoice link.' },
  ],
};
